import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Calendar,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Eye,
  X,
  CheckCircle,
  AlertCircle,
  Clock,
  User,
  Phone,
  Mail,
  Trash2,
  Edit,
  Save,
  Users,
  DollarSign,
  Building
} from 'lucide-react';
import axios from 'axios';

const ManageTableReservations = () => {
  const { user, token } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [currentPage, setCurrentPage] = useState(() => {
    const saved = localStorage.getItem('manageReservations_currentPage');
    return saved ? parseInt(saved, 10) : 1;
  });
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem('manageReservations_pageSize');
    return saved ? parseInt(saved, 10) : 10;
  });
  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem('manageReservations_filters');
    return saved ? JSON.parse(saved) : {
      date_filter: 'today',  // Default to "Today"
      status: '',
      category_id: ''
    };
  });
  const [tableCategories, setTableCategories] = useState([]);
  const [tables, setTables] = useState([]);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reservationToDelete, setReservationToDelete] = useState(null);
  const [formFields, setFormFields] = useState([]);
  const [businessCurrentDate, setBusinessCurrentDate] = useState('');
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [availableAlternatives, setAvailableAlternatives] = useState([]);
  const [conflictMessage, setConflictMessage] = useState('');
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [settings, setSettings] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const primaryColor = 'var(--theme-primary)';

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'checked_in', label: 'Checked In' },
    { value: 'checked_out', label: 'Checked Out' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'blocked', label: 'Blocked' }
  ];

  const dateFilterOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'tomorrow', label: 'Tomorrow' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'this_week', label: 'This Week' },
    { value: 'last_week', label: 'Last Week' },
    { value: 'this_month', label: 'This Month' }
  ];

  const statusColors = {
    confirmed: 'bg-green-100 text-green-800',
    checked_in: 'bg-blue-100 text-blue-800',
    checked_out: 'bg-gray-100 text-gray-800',
    cancelled: 'bg-red-100 text-red-800',
    blocked: 'bg-yellow-100 text-yellow-800'
  };

  // Save filter state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('manageReservations_filters', JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    localStorage.setItem('manageReservations_pageSize', pageSize.toString());
  }, [pageSize]);

  useEffect(() => {
    localStorage.setItem('manageReservations_currentPage', currentPage.toString());
  }, [currentPage]);

  useEffect(() => {
    fetchBusinessInfo();
    fetchSettings();
    fetchReservations();
    fetchTableCategories();
    fetchTables();
    fetchFormFields();
  }, [filters, currentPage, pageSize]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/public/table-settings`);
      if (response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchBusinessInfo = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/business-info`);
      if (response.data && response.data.current_time) {
        const [datePart] = response.data.current_time.split('T');
        setBusinessCurrentDate(datePart);
      }
    } catch (error) {
      console.error('Error fetching business info:', error);
    }
  };

  const fetchReservations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/table-reservations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        let filtered = response.data.reservations || [];
        
        // Apply filters
        if (filters.status) {
          filtered = filtered.filter(r => r.status === filters.status);
        }
        
        if (filters.category_id) {
          filtered = filtered.filter(r => {
            const table = tables.find(t => t.id === r.assigned_table_id);
            return table?.category_id === filters.category_id;
          });
        }
        
        // Date filtering
        if (filters.date_filter !== 'all') {
          // Use business current date if available, otherwise fall back to browser date
          let today;
          if (businessCurrentDate) {
            today = new Date(businessCurrentDate + 'T00:00:00');
          } else {
            today = new Date();
          }
          today.setHours(0, 0, 0, 0);
          
          filtered = filtered.filter(r => {
            // Append T00:00:00 to treat as local date and prevent timezone conversion
            const resDate = new Date(r.dropoff_date + 'T00:00:00');
            resDate.setHours(0, 0, 0, 0);
            
            switch (filters.date_filter) {
              case 'today':
                return resDate.getTime() === today.getTime();
              case 'tomorrow':
                const tomorrow = new Date(today);
                tomorrow.setDate(tomorrow.getDate() + 1);
                return resDate.getTime() === tomorrow.getTime();
              case 'yesterday':
                const yesterday = new Date(today);
                yesterday.setDate(yesterday.getDate() - 1);
                return resDate.getTime() === yesterday.getTime();
              case 'this_week':
                // Week starts on Sunday (0) and ends on Saturday (6)
                const thisWeekStart = new Date(today);
                thisWeekStart.setDate(today.getDate() - today.getDay());
                thisWeekStart.setHours(0, 0, 0, 0);
                const thisWeekEnd = new Date(thisWeekStart);
                thisWeekEnd.setDate(thisWeekStart.getDate() + 6);
                thisWeekEnd.setHours(23, 59, 59, 999);
                return resDate >= thisWeekStart && resDate <= thisWeekEnd;
              case 'last_week':
                // Last week: 7 days before this week's start to 1 day before this week's start
                const lastWeekEnd = new Date(today);
                lastWeekEnd.setDate(today.getDate() - today.getDay() - 1);
                lastWeekEnd.setHours(23, 59, 59, 999);
                const lastWeekStart = new Date(lastWeekEnd);
                lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
                lastWeekStart.setHours(0, 0, 0, 0);
                return resDate >= lastWeekStart && resDate <= lastWeekEnd;
              case 'this_month':
                return resDate.getMonth() === today.getMonth() && resDate.getFullYear() === today.getFullYear();
              default:
                return true;
            }
          });
        }
        
        // Sort by date descending
        filtered.sort((a, b) => new Date(b.dropoff_date + ' ' + b.dropoff_time) - new Date(a.dropoff_date + ' ' + a.dropoff_time));
        
        // Pagination
        const total = Math.ceil(filtered.length / pageSize);
        setTotalPages(total);
        const start = (currentPage - 1) * pageSize;
        const end = start + pageSize;
        setReservations(filtered.slice(start, end));
      }
    } catch (error) {
      console.error('Error fetching reservations:', error);
      showMessage('error', 'Failed to fetch reservations');
    } finally {
      setLoading(false);
    }
  };

  const fetchTableCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/table-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setTableCategories(response.data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchTables = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/tables`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setTables(response.data.tables || []);
      }
    } catch (error) {
      console.error('Error fetching tables:', error);
    }
  };

  const fetchFormFields = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/table-form-fields`);
      if (response.data.success) {
        setFormFields(response.data.fields || []);
      }
    } catch (error) {
      console.error('Error fetching form fields:', error);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleViewReservation = (reservation) => {
    setSelectedReservation(reservation);
    setEditForm({
      dropoff_date: reservation.dropoff_date,
      dropoff_time: reservation.dropoff_time,
      guest_count: reservation.guest_count,
      assigned_table_id: reservation.assigned_table_id,
      status: reservation.status
    });
    setShowDetailModal(true);
    setIsEditMode(false);
  };

  const handleDeleteReservation = async () => {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/api/table-reservations/${reservationToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showMessage('success', 'Reservation deleted successfully');
        fetchReservations();
        setShowDeleteModal(false);
        setReservationToDelete(null);
        if (showDetailModal) {
          setShowDetailModal(false);
        }
      }
    } catch (error) {
      showMessage('error', 'Failed to delete reservation');
    }
  };

  const handleEditReservation = () => {
    setIsEditMode(true);
  };

  const checkAvailabilityBeforeSave = async () => {
    setCheckingAvailability(true);
    
    try {
      // Check if date, time, or table has changed
      const hasChanged = 
        editForm.dropoff_date !== selectedReservation.dropoff_date ||
        editForm.dropoff_time !== selectedReservation.dropoff_time ||
        editForm.assigned_table_id !== selectedReservation.assigned_table_id;
      
      // If nothing changed related to scheduling, just save
      if (!hasChanged) {
        await saveReservationUpdate();
        return;
      }
      
      // Get duration from settings or reservation
      const duration = selectedReservation.duration_minutes || settings?.reservation_duration || 60;
      
      // Check availability for the new slot
      const response = await axios.post(
        `${API_BASE_URL}/api/table-reservations/check-availability`,
        {
          date: editForm.dropoff_date,
          time: editForm.dropoff_time,
          guest_count: editForm.guest_count,
          duration_minutes: duration,
          exclude_reservation_id: selectedReservation.id // Exclude current reservation from check
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.available) {
        // Check if the specific table is available
        const assignedTable = response.data.available_tables?.find(
          t => t.id === editForm.assigned_table_id
        );
        
        if (assignedTable) {
          // Table is available, proceed with save
          await saveReservationUpdate();
        } else {
          // Table is not available, show alternatives
          const selectedTable = tables.find(t => t.id === editForm.assigned_table_id);
          setConflictMessage(
            `${selectedTable?.table_name || 'Selected table'} is not available at ${editForm.dropoff_time} on ${new Date(editForm.dropoff_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}.`
          );
          setAvailableAlternatives(response.data.available_tables || []);
          setShowConflictModal(true);
        }
      } else {
        // No tables available at this time
        setConflictMessage(
          `No tables available at ${editForm.dropoff_time} on ${new Date(editForm.dropoff_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}. Please select a different date or time.`
        );
        setAvailableAlternatives([]);
        setShowConflictModal(true);
      }
    } catch (error) {
      console.error('Error checking availability:', error);
      showMessage('error', 'Failed to check availability. Please try again.');
    } finally {
      setCheckingAvailability(false);
    }
  };

  const saveReservationUpdate = async (customData = null) => {
    try {
      // Use customData if provided, otherwise use editForm
      const dataToSave = customData || editForm;
      
      const response = await axios.patch(
        `${API_BASE_URL}/api/table-reservations/${selectedReservation.id}`,
        dataToSave,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showMessage('success', 'Reservation updated successfully');
        setIsEditMode(false);
        fetchReservations();
        setShowDetailModal(false);
      }
    } catch (error) {
      console.error('Error updating reservation:', error);
      showMessage('error', error.response?.data?.detail || 'Failed to update reservation');
    }
  };

  const handleSelectAlternativeTable = (alternativeTable) => {
    // Create updated form data with new table
    const updatedForm = {
      ...editForm,
      assigned_table_id: alternativeTable.id,
      assigned_table_name: alternativeTable.table_name
    };
    
    // Update state for UI consistency
    setEditForm(updatedForm);
    setShowConflictModal(false);
    
    // Save with the updated data immediately (no race condition)
    saveReservationUpdate(updatedForm);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditForm({
      dropoff_date: selectedReservation.dropoff_date,
      dropoff_time: selectedReservation.dropoff_time,
      guest_count: selectedReservation.guest_count,
      assigned_table_id: selectedReservation.assigned_table_id,
      status: selectedReservation.status
    });
  };

  const formatStatus = (status) => {
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formatDate = (dateStr) => {
    // Append T00:00:00 to treat as local date and prevent timezone conversion
    const date = new Date(dateStr + 'T00:00:00');
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    
    const getOrdinalSuffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    return `${day}${getOrdinalSuffix(day)} ${month}`;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes}${ampm}`;
  };

  const getFieldDisplayName = (fieldId) => {
    const field = formFields.find(f => f.id === fieldId);
    if (field) return field.field_name;
    // Convert snake_case to Title Case
    return fieldId.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
          {/* Header */}
          <div className="bg-white border-b border-gray-200">
            <div className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-3">
                    Manage Table Reservations
                    <span className="text-xs font-normal text-gray-500">
                      Total: {reservations.length}
                    </span>
                  </h2>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  {/* Category Filter */}
                  <select
                    value={filters.category_id}
                    onChange={(e) => {
                      setFilters({ ...filters, category_id: e.target.value });
                      setCurrentPage(1);
                    }}
                    className="text-white text-sm px-3 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-1 transition-colors appearance-none pr-8"
                    style={{ 
                      backgroundColor: primaryColor,
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 6px center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '14px'
                    }}
                  >
                    <option value="" style={{ backgroundColor: 'white', color: 'black' }}>All Categories</option>
                    {tableCategories.map((category) => (
                      <option key={category.id} value={category.id} style={{ backgroundColor: 'white', color: 'black' }}>
                        {category.category_name}
                      </option>
                    ))}
                  </select>

                  {/* Date Filter */}
                  <select
                    value={filters.date_filter}
                    onChange={(e) => {
                      setFilters({ ...filters, date_filter: e.target.value });
                      setCurrentPage(1);
                    }}
                    className="text-white text-sm px-3 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-1 transition-colors appearance-none pr-8"
                    style={{ 
                      backgroundColor: primaryColor,
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 6px center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '14px'
                    }}
                  >
                    {dateFilterOptions.map((option) => (
                      <option key={option.value} value={option.value} style={{ backgroundColor: 'white', color: 'black' }}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  {/* Status Filter */}
                  <select
                    value={filters.status}
                    onChange={(e) => {
                      setFilters({ ...filters, status: e.target.value });
                      setCurrentPage(1);
                    }}
                    className="text-white text-sm px-3 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-1 transition-colors appearance-none pr-8"
                    style={{ 
                      backgroundColor: primaryColor,
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 6px center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '14px'
                    }}
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value} style={{ backgroundColor: 'white', color: 'black' }}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  {/* Page Size Selector */}
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="text-white text-sm px-3 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-1 transition-colors appearance-none pr-8"
                    style={{ 
                      backgroundColor: primaryColor,
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 6px center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '14px'
                    }}
                  >
                    <option value="10" style={{ backgroundColor: 'white', color: 'black' }}>10 per page</option>
                    <option value="25" style={{ backgroundColor: 'white', color: 'black' }}>25 per page</option>
                    <option value="50" style={{ backgroundColor: 'white', color: 'black' }}>50 per page</option>
                    <option value="100" style={{ backgroundColor: 'white', color: 'black' }}>100 per page</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Message Banner */}
          {message.text && (
            <div className={`px-4 py-3 ${message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border-b`}>
              <p className={`text-sm ${message.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                {message.text}
              </p>
            </div>
          )}

          {/* Reservations Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: primaryColor }}></div>
              </div>
            ) : reservations.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No reservations found</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Table
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Guests
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reservations.map((reservation) => {
                    const table = tables.find(t => t.id === reservation.assigned_table_id);
                    const category = tableCategories.find(c => c.id === table?.category_id);
                    
                    return (
                      <tr key={reservation.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {formatDate(reservation.dropoff_date)}
                              </div>
                              <div className="text-xs text-gray-500">
                                {formatTime(reservation.dropoff_time)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">
                            {reservation.form_details?.first_name || reservation.form_details?.name || 'N/A'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {reservation.form_details?.email || reservation.form_details?.phone || ''}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">
                            {reservation.assigned_table_name || 'N/A'}
                          </div>
                          {category && (
                            <div className="text-xs text-gray-500">
                              {category.category_name}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1 text-sm text-gray-900">
                            <Users className="h-4 w-4 text-gray-400" />
                            {reservation.guest_count || 0}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[reservation.status] || 'bg-gray-100 text-gray-800'}`}>
                            {formatStatus(reservation.status)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleViewReservation(reservation)}
                              className="p-1.5 text-gray-600 hover:text-theme-blue hover:bg-blue-50 rounded-md transition-colors"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                setReservationToDelete(reservation);
                                setShowDeleteModal(true);
                              }}
                              className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reservation Detail Modal */}
      {showDetailModal && selectedReservation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200" style={{ backgroundColor: primaryColor }}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    {isEditMode ? 'Edit Reservation' : 'Reservation Details'}
                  </h3>
                  <p className="text-sm text-white text-opacity-90 mt-0.5">
                    #{selectedReservation.reservation_number || selectedReservation.id?.slice(0, 8)}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setIsEditMode(false);
                  }}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              {/* Date & Time */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="text-xs font-semibold text-blue-700 uppercase mb-2 flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    Date & Time
                  </div>
                  {isEditMode ? (
                    <div className="space-y-2">
                      <input
                        type="date"
                        value={editForm.dropoff_date}
                        onChange={(e) => setEditForm({...editForm, dropoff_date: e.target.value})}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      />
                      <input
                        type="time"
                        value={editForm.dropoff_time}
                        onChange={(e) => setEditForm({...editForm, dropoff_time: e.target.value})}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      />
                    </div>
                  ) : (
                    <>
                      <div className="text-sm font-semibold text-gray-900">
                        {new Date(selectedReservation.dropoff_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                      </div>
                      <div className="text-sm text-gray-700 mt-1">
                        {formatTime(selectedReservation.dropoff_time)}
                      </div>
                    </>
                  )}
                </div>

                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="text-xs font-semibold text-green-700 uppercase mb-2 flex items-center gap-1">
                    <Users className="h-3.5 w-3.5" />
                    Party Details
                  </div>
                  {isEditMode ? (
                    <input
                      type="number"
                      value={editForm.guest_count}
                      onChange={(e) => setEditForm({...editForm, guest_count: parseInt(e.target.value)})}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      min="1"
                    />
                  ) : (
                    <div className="text-sm font-semibold text-gray-900">
                      {selectedReservation.guest_count} {selectedReservation.guest_count === 1 ? 'Guest' : 'Guests'}
                    </div>
                  )}
                </div>
              </div>

              {/* Table Assignment */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Assigned Table</h4>
                {isEditMode ? (
                  <select
                    value={editForm.assigned_table_id}
                    onChange={(e) => {
                      const selectedTable = tables.find(t => t.id === e.target.value);
                      setEditForm({
                        ...editForm, 
                        assigned_table_id: e.target.value,
                        assigned_table_name: selectedTable?.table_name || ''
                      });
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  >
                    <option value="">Select Table</option>
                    {tables.map(table => {
                      const category = tableCategories.find(c => c.id === table.category_id);
                      return (
                        <option key={table.id} value={table.id}>
                          {table.table_name} {category ? `(${category.category_name})` : ''}
                        </option>
                      );
                    })}
                  </select>
                ) : (
                  <>
                    <div className="text-sm font-medium text-gray-900">
                      {selectedReservation.assigned_table_name || 'N/A'}
                    </div>
                    {(() => {
                      const table = tables.find(t => t.id === selectedReservation.assigned_table_id);
                      const category = tableCategories.find(c => c.id === table?.category_id);
                      return category && (
                        <div className="text-xs text-gray-500 mt-1">
                          {category.category_name}
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>

              {/* Customer Information */}
              {selectedReservation.form_details && Object.keys(selectedReservation.form_details).length > 0 && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Customer Information</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(selectedReservation.form_details)
                      .filter(([key]) => key !== 'guest_count')
                      .map(([key, value]) => (
                        <div key={key}>
                          <label className="text-xs font-medium text-gray-500">
                            {getFieldDisplayName(key)}
                          </label>
                          <p className="text-sm text-gray-900 mt-0.5">{value || 'N/A'}</p>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Special Instructions */}
              {selectedReservation.special_instructions && Object.keys(selectedReservation.special_instructions).length > 0 && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Special Requests</h4>
                  <div className="space-y-2">
                    {Object.entries(selectedReservation.special_instructions).map(([key, value]) => (
                      <div key={key}>
                        <label className="text-xs font-medium text-gray-500">
                          {getFieldDisplayName(key)}
                        </label>
                        <p className="text-sm text-gray-900 mt-0.5 whitespace-pre-wrap">{value || 'N/A'}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Status */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Status</h4>
                {isEditMode ? (
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  >
                    <option value="confirmed">Confirmed</option>
                    <option value="checked_in">Checked In</option>
                    <option value="checked_out">Checked Out</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                ) : (
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusColors[selectedReservation.status] || 'bg-gray-100 text-gray-800'}`}>
                    {formatStatus(selectedReservation.status)}
                  </span>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
              {isEditMode ? (
                <>
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={checkAvailabilityBeforeSave}
                    disabled={checkingAvailability}
                    className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {checkingAvailability ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Checking...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setReservationToDelete(selectedReservation);
                      setShowDeleteModal(true);
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                  <button
                    onClick={handleEditReservation}
                    className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-2"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <Edit className="h-4 w-4" />
                    Edit Reservation
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && reservationToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Reservation</h3>
                  <p className="text-sm text-gray-500 mt-0.5">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-6">
                Are you sure you want to delete this reservation for{' '}
                <span className="font-semibold">{reservationToDelete.form_details?.first_name || 'this customer'}</span>?
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setReservationToDelete(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteReservation}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Availability Conflict Modal */}
      {showConflictModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-yellow-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                    <AlertCircle className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Table Not Available</h3>
                    <p className="text-sm text-gray-600 mt-0.5">Scheduling conflict detected</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowConflictModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-700">{conflictMessage}</p>
              </div>

              {availableAlternatives.length > 0 ? (
                <>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    Available Tables at This Time:
                  </h4>
                  <div className="space-y-2">
                    {availableAlternatives.map((table) => {
                      const category = tableCategories.find(c => c.id === table.category_id);
                      return (
                        <button
                          key={table.id}
                          onClick={() => handleSelectAlternativeTable(table)}
                          className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-theme-blue hover:bg-blue-50 transition-all text-left group"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-semibold text-gray-900 group-hover:text-theme-blue">
                                {table.table_name}
                              </div>
                              {category && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {category.category_name}
                                </div>
                              )}
                              <div className="text-xs text-gray-500 mt-1">
                                Capacity: {table.capacity} guests
                              </div>
                            </div>
                            <CheckCircle className="h-5 w-5 text-gray-400 group-hover:text-theme-blue" />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500 mt-4">
                    Click on a table to reassign this reservation
                  </p>
                </>
              ) : (
                <div className="text-center py-8">
                  <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm text-gray-600">
                    No alternative tables available at this time.
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    Please select a different date or time slot.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowConflictModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Change Date/Time
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ManageTableReservations;
